import React, { forwardRef, useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import CheckIcon2 from '@/shared/ui/icon/CheckIcon2';
import { sanitizeChatHtml } from '@/shared/lib/sanitizeChatHtml';

const DemoGuideMessage = forwardRef<
  HTMLDivElement,
  {
    shouldAnimate: boolean;
    setIsGuideMessageDone: React.Dispatch<React.SetStateAction<boolean>>;
  }
>(({ shouldAnimate, setIsGuideMessageDone }, ref) => {
  const t = useTranslations('Demo');
  const heading1Array = t(`Winging이 학습을 시작합니다`).split(' ');
  const [headingOneVisibleWordCount, setHeadingOneVisibleWordCount] =
    useState<number>(0);

  const firstLiArray = t(
    'Winging이 학습할, 제품 정보를 담은 URL 또는 문서(지원 가능한 형식 : PDF, HWP, HWPX, DOC, DOCX)를 업로드 해주세요! 업로드 하신 데이터를 바탕으로 Winging이 스스로 학습합니다'
  ).split(' ');
  const [firstLiVisibleWordCount, setFirstLiVisibleWordCount] =
    useState<number>(0);

  const secondLiArray = t(
    '데모 버전에서는 최대 3개 데이터까지 등록해서, Winging을 체험하실 수 있어요 정식 서비스 구독을 하시면, 더 많은 데이터를 업로드하고 학습 시킬 수 있어요'
  ).split(' ');
  const [secondLiVisibleWordCount, setSecondLiVisibleWordCount] =
    useState<number>(0);

  const heading2Array = t(
    '데이터 업로드 전에 아래 내용을 꼭 확인해 주세요'
  ).split(' ');
  const [headingTwoVisibleWordCount, setHeadingTwoVisibleWordCount] =
    useState<number>(0);

  const thirdLiArray = t(
    '업로드할 데이터에 민감 개인정보가 포함된 경우 반드시 삭제/마스킹 처리 후 업로드 하셔야 합니다 이를 이행하지 않아 발생하는 문제에 대해서는 윌콘에이아이 주식회사가 어떤 책임도 지지 않음을 꼭 유의해 주시기 바랍니다'
  ).split(' ');
  const [thirdLiVisibleWordCount, setThirdLiVisibleWordCount] =
    useState<number>(0);

  const fourthLiArray = [
    ...t(
      '업로드하신 데이터에 이미지가 많은 경우, 최대 50개의 이미지만 학습됩니다 또한 파일 분량이 큰 경우에는 일부 내용이 잘려 학습될 수 있습니다'
    ).split(' '),
  ];
  const [fourthLiVisibleWordCount, setFourthLiVisibleWordCount] =
    useState<number>(0);

  useEffect(() => {
    if (!shouldAnimate) {
      return;
    }
    const interval = setInterval(() => {
      setHeadingOneVisibleWordCount((prev) => {
        if (prev >= heading1Array.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [shouldAnimate]);

  useEffect(() => {
    if (headingOneVisibleWordCount !== heading1Array.length) {
      return;
    }
    const interval = setInterval(() => {
      setFirstLiVisibleWordCount((prev) => {
        if (prev >= firstLiArray.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [headingOneVisibleWordCount]);

  useEffect(() => {
    if (firstLiVisibleWordCount !== firstLiArray.length) {
      return;
    }
    const interval = setInterval(() => {
      setSecondLiVisibleWordCount((prev) => {
        if (prev >= secondLiArray.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [firstLiVisibleWordCount]);

  useEffect(() => {
    if (secondLiVisibleWordCount !== secondLiArray.length) {
      return;
    }
    const interval = setInterval(() => {
      setHeadingTwoVisibleWordCount((prev) => {
        if (prev >= heading2Array.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [secondLiVisibleWordCount]);

  useEffect(() => {
    if (headingTwoVisibleWordCount !== heading2Array.length) {
      return;
    }
    const interval = setInterval(() => {
      setThirdLiVisibleWordCount((prev) => {
        if (prev >= thirdLiArray.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [headingTwoVisibleWordCount]);

  useEffect(() => {
    if (thirdLiVisibleWordCount !== thirdLiArray.length) {
      return;
    }
    const interval = setInterval(() => {
      setFourthLiVisibleWordCount((prev) => {
        if (prev >= fourthLiArray.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [thirdLiVisibleWordCount]);

  // 모든 애니메이션이 완료되었을 때 부모 컴포넌트에 알림
  useEffect(() => {
    if (
      fourthLiVisibleWordCount === fourthLiArray.length &&
      fourthLiArray.length > 0
    ) {
      setIsGuideMessageDone(true);
    }
  }, [fourthLiVisibleWordCount, fourthLiArray.length, setIsGuideMessageDone]);

  return (
    <div
      className="answer animate-fade-in main:mt-[-50px] mt-[-26px]"
      ref={ref}
    >
      <div className="flex items-start gap-[38px]">
        <div className="text-label1 main:ml-[138px] main:px-[20px] main:text-body2 main:font-[500] ml-[86px] block max-w-[min(734px,90%)] rounded-tl-md rounded-tr-md rounded-br-md border border-gray-100 bg-white px-[10px] py-[10px] text-left font-[400] wrap-break-word text-gray-800">
          {!!headingOneVisibleWordCount && (
            <div className={''}>
              <strong className={'font-[700]'}>
                {heading1Array
                  .slice(0, headingOneVisibleWordCount)
                  .map((word, i) => {
                    if (word === '<br>' || word === '\n') {
                      return <br key={i} />;
                    }
                    return (
                      <span key={i} className="animate-fade-in transition-all">
                        {word + ' '}
                      </span>
                    );
                  })}
              </strong>
            </div>
          )}
          {!!firstLiVisibleWordCount && (
            <div className={'mt-[8px]'}>
              <ul className={'pl-[30px]'}>
                <li className={'list-disc'}>
                  {firstLiArray
                    .slice(0, firstLiVisibleWordCount)
                    .map((word, i) => {
                      if (word === '<br>' || word === '\n') {
                        return <br key={i} />;
                      }
                      return (
                        <span
                          key={i}
                          className="animate-fade-in transition-all"
                        >
                          {word + ' '}
                        </span>
                      );
                    })}
                </li>
                {!!secondLiVisibleWordCount && (
                  <li className={'list-disc'}>
                    {secondLiArray
                      .slice(0, secondLiVisibleWordCount)
                      .map((word, i) => {
                        if (word === '<br>' || word === '\n') {
                          return <br key={i} />;
                        }
                        return (
                          <span
                            key={i}
                            className="animate-fade-in transition-all"
                          >
                            {word + ' '}
                          </span>
                        );
                      })}
                  </li>
                )}
              </ul>
            </div>
          )}
          {!!headingTwoVisibleWordCount && (
            <div className={'mt-[12px]'}>
              <strong className={'font-[700]'}>
                {heading2Array
                  .slice(0, headingTwoVisibleWordCount)
                  .map((word, i) => {
                    if (word === '<br>' || word === '\n') {
                      return <br key={i} />;
                    }
                    return (
                      <span key={i} className="animate-fade-in transition-all">
                        {word + ' '}
                      </span>
                    );
                  })}
              </strong>
            </div>
          )}
          {!!thirdLiVisibleWordCount && (
            <div className={'mt-[8px]'}>
              <ul className={'pl-[0px]'}>
                <li className={'flex items-start'}>
                  <CheckIcon2
                    size={20}
                    className={'mr-[10px] h-auto min-w-[20px]'}
                  />{' '}
                  <div className={'flex flex-wrap'}>
                    {thirdLiArray
                      .slice(0, thirdLiVisibleWordCount)
                      .map((word, i) => {
                        if (word === '<br>' || word === '\n') {
                          return <br key={i} />;
                        }
                        return (
                          <span
                            key={i}
                            className="animate-fade-in whitespace-pre transition-all"
                          >
                            {word + ' '}
                          </span>
                        );
                      })}
                  </div>
                </li>
                {!!fourthLiVisibleWordCount && (
                  <li className={'flex items-start'}>
                    <CheckIcon2
                      size={20}
                      className={'mr-[10px] h-auto min-w-[20px]'}
                    />{' '}
                    <div className={'flex flex-wrap'}>
                      {fourthLiArray
                        .slice(0, fourthLiVisibleWordCount)
                        .map((word, i) => {
                          if (word === '<br>' || word === '\n') {
                            return <br key={i} />;
                          }
                          return (
                            <span
                              key={i}
                              className="animate-fade-in whitespace-pre transition-all"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeChatHtml(word + ' '),
                              }}
                            ></span>
                          );
                        })}
                    </div>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
DemoGuideMessage.displayName = 'DemoGuideMessage'; // 👈 이 줄 추가

export default DemoGuideMessage;
